import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Activity, TrendingUp, TrendingDown, Users, Calendar, Loader2 } from "lucide-react";

const RANGES = [
  { v: 7, label: "7 giorni" },
  { v: 30, label: "30 giorni" },
  { v: 90, label: "3 mesi" },
  { v: 180, label: "6 mesi" },
  { v: 365, label: "1 anno" },
];

export default function AnalyticsCard({ sedi = [] }) {
  const [range, setRange] = useState(30);
  const [sedeId, setSedeId] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { range_days: range };
      if (sedeId) params.sede_id = sedeId;
      const { data } = await api.get("/medico/analytics/visits-trend", { params });
      setData(data);
    } catch { setData(null); }
    finally { setLoading(false); }
  }, [range, sedeId]);

  useEffect(() => { load(); }, [load]);

  const max = data?.daily?.length ? Math.max(...data.daily.map((x) => x.visits), 1) : 1;
  const delta = data?.delta_pct;
  const deltaPositive = delta !== null && delta !== undefined && delta >= 0;

  return (
    <div className="bg-white border border-[#E2E8F0] rounded-xl p-5 mb-6" data-testid="analytics-card">
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <Activity className="text-brand-azure" size={18} />
        <h3 className="font-extrabold text-brand-navy">Andamento attività</h3>
        <span className="text-xs text-slate-500 ml-1">tenant isolato (solo le tue visite)</span>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <select
            data-testid="analytics-range"
            value={range}
            onChange={(e) => setRange(parseInt(e.target.value, 10))}
            className="px-3 py-1.5 rounded-lg border border-[#E2E8F0] text-sm bg-white font-bold text-brand-navy"
          >
            {RANGES.map((r) => <option key={r.v} value={r.v}>{r.label}</option>)}
          </select>
          {sedi.length > 0 && (
            <select
              data-testid="analytics-sede"
              value={sedeId}
              onChange={(e) => setSedeId(e.target.value)}
              className="px-3 py-1.5 rounded-lg border border-[#E2E8F0] text-sm bg-white"
            >
              <option value="">Tutte le sedi</option>
              {sedi.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          )}
        </div>
      </div>

      {loading ? (
        <div className="py-8 text-center text-slate-500"><Loader2 className="animate-spin mx-auto mb-2" size={20} /> Caricamento…</div>
      ) : !data ? (
        <div className="py-6 text-center text-slate-500 italic text-sm">Nessun dato disponibile</div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <div className="bg-brand-navy text-white rounded-lg p-3" data-testid="analytics-total">
              <div className="text-[10px] uppercase font-bold tracking-wider opacity-80">Visite totali</div>
              <div className="text-2xl font-extrabold tabular-nums mt-1">{data.total_visits}</div>
              {delta !== null && delta !== undefined && (
                <div className={`text-[10px] mt-1 flex items-center gap-0.5 font-bold ${deltaPositive ? "text-emerald-200" : "text-orange-200"}`}>
                  {deltaPositive ? <TrendingUp size={10} /> : <TrendingDown size={10} />}
                  {deltaPositive ? "+" : ""}{delta}% vs precedente
                </div>
              )}
            </div>
            <div className="bg-brand-azure text-white rounded-lg p-3" data-testid="analytics-unique">
              <div className="text-[10px] uppercase font-bold tracking-wider opacity-80 flex items-center gap-1"><Users size={10} /> Pazienti unici</div>
              <div className="text-2xl font-extrabold tabular-nums mt-1">{data.unique_patients}</div>
              <div className="text-[10px] mt-1 opacity-80">visite ripetute incl.</div>
            </div>
            <div className="bg-[#9FD8EA] text-[#1E3A5F] rounded-lg p-3" data-testid="analytics-avg">
              <div className="text-[10px] uppercase font-bold tracking-wider opacity-80 flex items-center gap-1"><Calendar size={10} /> Media/giorno</div>
              <div className="text-2xl font-extrabold tabular-nums mt-1">{data.avg_per_day}</div>
              <div className="text-[10px] mt-1 opacity-80">su {data.range_days} giorni</div>
            </div>
            <div className="bg-white border border-[#E2E8F0] rounded-lg p-3" data-testid="analytics-period">
              <div className="text-[10px] uppercase font-bold tracking-wider text-slate-500">Periodo</div>
              <div className="text-sm font-extrabold text-brand-navy mt-1">{data.start?.slice(8,10)}/{data.start?.slice(5,7)} → {data.end?.slice(8,10)}/{data.end?.slice(5,7)}</div>
              <div className="text-[10px] mt-1 text-slate-500">Precedente: {data.previous_total} visite</div>
            </div>
          </div>

          {data.daily?.length > 0 ? (
            <div className="flex items-end gap-1 h-28 overflow-x-auto" data-testid="analytics-chart">
              {data.daily.map((t) => (
                <div key={t.day} className="flex flex-col items-center gap-1 min-w-[14px] flex-shrink-0" title={`${t.day}: ${t.visits} visite, ${t.unique_patients} pazienti unici`}>
                  <div className="text-[9px] font-bold text-slate-500">{t.visits || ""}</div>
                  <div className="w-full bg-brand-azure rounded-t" style={{ height: `${(t.visits / max) * 88}px`, minHeight: t.visits ? 4 : 1, opacity: t.visits ? 1 : 0.15, width: range > 90 ? 6 : 14 }}></div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-xs text-slate-400 italic py-4 text-center">Nessuna visita nel periodo selezionato</div>
          )}
        </>
      )}
    </div>
  );
}
